import { useState, type ReactElement } from 'react';
import {
  Accessibility,
  AirVent,
  BriefcaseBusiness,
  CarFront,
  ChevronDown,
  ChevronUp,
  Circle,
  Coffee,
  Dumbbell,
  Martini,
  PawPrint,
  Sparkles,
  Utensils,
  UsersRound,
  Waves,
  Wifi,
} from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import type { HotelDetailsAmenitySectionView } from '../models.js';

const COLLAPSED_AMENITIES = 6;

function amenityIcon(
  icon: HotelDetailsAmenitySectionView['amenities'][number]['icon'],
): ReactElement {
  switch (icon) {
    case 'wifi':
      return <Wifi />;
    case 'pool':
      return <Waves />;
    case 'bar':
      return <Martini />;
    case 'parking':
      return <CarFront />;
    case 'fitness':
      return <Dumbbell />;
    case 'restaurant':
      return <Utensils />;
    case 'air-conditioning':
      return <AirVent />;
    case 'breakfast':
      return <Coffee />;
    case 'business':
      return <BriefcaseBusiness />;
    case 'family':
      return <UsersRound />;
    case 'pets':
      return <PawPrint />;
    case 'spa':
      return <Sparkles />;
    case 'accessibility':
      return <Accessibility />;
    case 'other':
    default:
      return <Circle />;
  }
}

export function AmenityGlyph({
  icon,
}: {
  readonly icon: HotelDetailsAmenitySectionView['amenities'][number]['icon'];
}) {
  return (
    <span aria-hidden="true" data-icon={icon}>
      {amenityIcon(icon)}
    </span>
  );
}

export function HotelDetailsAmenities({
  sections,
  allAmenitiesLabel,
  fewerAmenitiesLabel,
}: {
  readonly sections: readonly HotelDetailsAmenitySectionView[];
  readonly allAmenitiesLabel: string;
  readonly fewerAmenitiesLabel: string;
}) {
  if (sections.length === 0) return null;

  return (
    <section className="genui2 hui-hotel-details-amenities">
      {sections.map((section) => (
        <HotelDetailsAmenitySection
          key={section.id}
          section={section}
          allAmenitiesLabel={allAmenitiesLabel}
          fewerAmenitiesLabel={fewerAmenitiesLabel}
        />
      ))}
    </section>
  );
}

function HotelDetailsAmenitySection({
  section,
  allAmenitiesLabel,
  fewerAmenitiesLabel,
}: {
  readonly section: HotelDetailsAmenitySectionView;
  readonly allAmenitiesLabel: string;
  readonly fewerAmenitiesLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <section className="hui-hotel-details-amenity-section">
      <h3>{section.title}</h3>
      <ul>
        {section.amenities.slice(0, expanded ? undefined : COLLAPSED_AMENITIES).map((amenity) => (
          <li key={amenity.id}>
            <AmenityGlyph icon={amenity.icon} />
            {amenity.label}
          </li>
        ))}
      </ul>
      {section.amenities.length > COLLAPSED_AMENITIES && (
        <Button
          variant="ghost"
          className="hui-hotel-details-all-amenities"
          aria-expanded={expanded}
          onClick={() => {
            setExpanded((current) => !current);
          }}
        >
          {expanded ? fewerAmenitiesLabel : allAmenitiesLabel}
          {expanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
        </Button>
      )}
    </section>
  );
}
